import { useState } from "react";
import { ChevronLeft } from "lucide-react";
import { BrandMark, Wordmark, ProBadge, Icon } from "@cntrl/ui";
import { useMobileBackStore } from "../../app/mobileBack.js";
import { useMe } from "../../app/hooks/useSession.js";
import { NotificationBell } from "../../features/notifications/NotificationBell.js";
import { FixtureSwitchButton } from "../../features/dev/FixtureSwitchButton.js";
import { AccountSheet } from "./AccountSheet.js";
import { Avatar } from "./Avatar.js";
import { initials } from "./accountRoles.js";
import { mobileSectionTitle } from "./mobileSectionTitle.js";
import styles from "./MobileShell.module.scss";
import { useAvatarUrl } from "../../app/hooks/useAvatar.js";

export interface MobileHeaderProps {
  /** Current pathname — the brand wordmark shows only on the home tab (parity with the MVP m-header). */
  activeRoute: string;
  /** The resolved active band (shared with the shell) — the seat the account sheet checks. */
  activeBandId: string | undefined;
  /** Whether the mounted band is on Pro. The badge rides the wordmark, which is already home-only, so the
      "PRO lockup is HOME ONLY — never badge the whole app" rule needs no second condition here. */
  isPro?: boolean;
  onNavigate?: (route: string) => void;
  onLogout: () => void;
  /** Whether the dev fixture-switch dialog is open (owned by the shell — the dialog mounts at shell root). */
  fixtureSwitchOpen?: boolean;
  /** Open the dev fixture-switch dialog. The button is here in the chrome; the dialog is at shell root. */
  onOpenFixtureSwitch?: () => void;
}

/**
 * The persistent mobile header (+ `MobileHeader`) — the top chrome
 * the MobileShell was missing. Left→right: the crimson brand MARK on every route + the "Artist CNTRL"
 * WORDMARK on home only (off-home the routed screen owns its own title); then the notifications BELL (unread
 * badge) and the profile AVATAR that opens the account/band-switcher sheet.
 *
 * The bell is the shared `NotificationBell`, pinned to its navigate-to-the-page mode (`allowPanel={false}`)
 * rather than the anchored panel — the URL-addressable page already exists, and it is what the desktop
 * shell also falls back to below `md`. The WCAG 4.1.2 note this header used to carry (the unread count
 * belongs in the BUTTON's accessible name, not on a badge span inside it) moved into that component with
 * the markup, and was the defect the extraction propagated to the other two shells.
 */
export function MobileHeader({
  activeRoute,
  activeBandId,
  isPro,
  onNavigate,
  onLogout,
  fixtureSwitchOpen = false,
  onOpenFixtureSwitch,
}: MobileHeaderProps) {
  const me = useMe();
  const [accountOpen, setAccountOpen] = useState(false);
  // A drilled-in screen registers a back handler; when one is present the leading slot BECOMES a back chevron
  // in place of the logo (PO, 2026-09-14). A tab root registers nothing, so it keeps the logo.
  const back = useMobileBackStore((s) => s.handler);

  const isHome = activeRoute === "/";
  // Off home, the header carries the section name beside the logo (ART-186/187…), and the routed screen drops
  // its own title. Home keeps the wordmark instead.
  const sectionTitle = mobileSectionTitle(activeRoute);
  const person = me.data?.person;
  // The active band seat, and whether it is a BAND IDENTITY (the owner seat — "the artist IS the band").
  // A band account's photo lives on the BAND, not the person (whose `avatarMediaId` is null), so its chip has
  // to resolve the band avatar or it falls to initials — the desktop `BandSwitcher` already does this.
  const activeSeat = me.data?.seats?.find((s) => s.kind === "band" && s.bandId === activeBandId);
  const isBandIdentity = activeSeat?.isOwner === true;
  const avatarMark = initials(
    isBandIdentity ? activeSeat?.displayName : person?.displayName || person?.firstName,
  );
  // Your own face — or, for a band identity, the band's photo. Either rides the authenticated global avatar
  // route (`GET /avatars/{kind}/{id}`, keyed by `avatarMediaId`); `person.avatarUrl` is the future CDN slot
  // and is always null, so a chip reading it could never show an uploaded photo. The local `Avatar` takes a
  // resolved url, so the resolution happens here.
  const selfAvatarUrl = useAvatarUrl(
    isBandIdentity ? "band" : "person",
    isBandIdentity ? activeSeat?.bandId : person?.personId,
    isBandIdentity ? activeSeat?.avatarMediaId : person?.avatarMediaId,
  );

  return (
    <>
      <header className={styles.header}>
        <div className={styles.headerLead}>
          {back ? (
            // Logo-replacement back: the drilled-in screen's back handler takes the leading slot. The section
            // title stays beside it, so the bar reads `‹ CONTACTS` — a native leading-nav bar.
            <button type="button" className={styles.headerBack} aria-label="Back" onClick={() => back()}>
              <Icon icon={ChevronLeft} size={24} />
            </button>
          ) : (
            <button
              type="button"
              className={styles.headerBrand}
              aria-label="Artist CNTRL home"
              onClick={() => onNavigate?.("/")}
            >
              <BrandMark size={26} />
              {isHome && (
                <span className={styles.headerWordmark}>
                  {/* Sized to match the 18px section title (`.headerSection`): the wordmark is caps-only, so
                      its rendered HEIGHT tracks the title's cap height rather than its font-size. */}
                  <Wordmark size={14} />
                  {isPro && <ProBadge size={13} />}
                </span>
              )}
            </button>
          )}
          {/* The section title sits BESIDE the logo/back (not inside the home button — tapping it must not
              navigate home). Only off-home, and only for a route the map knows. */}
          {!isHome && sectionTitle && <span className={styles.headerSection}>{sectionTitle}</span>}
        </div>

        <div className={styles.headerActions}>
          {/* The DEV "view as" switcher (ART-168) — renders nothing outside a non-production web lane / for a
              non-fixture account, so it is absent from every real mobile session and on native by construction.
              The button carries the acting-as glow (this header is always on screen); the dialog it opens is
              mounted at MobileShell root, because `Modal` does not portal and this header is a stacking context. */}
          <FixtureSwitchButton open={fixtureSwitchOpen} onOpen={() => onOpenFixtureSwitch?.()} />
          <NotificationBell
            // Never the inline panel: this is the mobile chrome, and a Capacitor wrapper on a tablet is
            // wide enough to pass the viewport check while having nowhere sensible to anchor a popover.
            allowPanel={false}
            onNavigate={onNavigate}
          />

          <button
            type="button"
            className={styles.headerAvatarBtn}
            aria-haspopup="dialog"
            aria-expanded={accountOpen}
            aria-label="Account menu"
            onClick={() => setAccountOpen(true)}
          >
            <Avatar url={selfAvatarUrl} mark={avatarMark} square={isBandIdentity} />
          </button>
        </div>
      </header>

      {accountOpen && (
        <AccountSheet
          person={person}
          seats={me.data?.seats ?? []}
          activeBandId={activeBandId}
          onClose={() => setAccountOpen(false)}
          onLogout={onLogout}
          onNavigate={onNavigate}
        />
      )}
    </>
  );
}
